import { JobStatus } from '@seek/hirer-job-information';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Box,
  Inline,
  Strong,
  TextLinkButton,
  Badge,
  Stack,
} from 'braid-design-system';
import { type ComponentProps, type FC, useState } from 'react';

import { ReferenceCheckBalanceDialog } from 'src/App/AppPageBlock/ReferenceCheckBalanceDialog';
import { Display } from 'src/libs/Display';

import translations from './.vocab/';

export const JobExpireDate = ({
  jobStatus,
  numberOfDaysRemaining,
}: {
  jobStatus: JobStatus;
  numberOfDaysRemaining: number | null;
}) => {
  const { t } = useTranslations(translations);

  if (numberOfDaysRemaining === null) {
    return null;
  }
  if (JobStatus.ACTIVE === jobStatus) {
    if (numberOfDaysRemaining === 0) {
      return (
        <Text tone="secondary" size="small">
          {t('Expires today')}
        </Text>
      );
    }
    return (
      <Text size="small" tone="secondary">
        {t('statNumber Days left', {
          statNumber: numberOfDaysRemaining,
          Strong: (children) => <Strong>{children}</Strong>,
        })}
      </Text>
    );
  }
  return null;
};

export const TotalApplications = ({
  totalApplications,
}: {
  totalApplications: number;
}) => {
  const { t } = useTranslations(translations);

  return (
    <Text size="small" tone="secondary">
      {t('statNumber Applications', {
        statNumber: totalApplications,
        Strong: (children) => <Strong>{children}</Strong>,
      })}
    </Text>
  );
};

interface BillableApplicantsLinkProps {
  billableApplicants?:
    | {
        success: true;
        billableApplicantsCount: number;
        maxBillableApplicants: number;
      }
    | {
        success: false;
      };
  openRelevantApplicationDialog: () => void;
}

export const BillableApplicantsLink: FC<BillableApplicantsLinkProps> = ({
  billableApplicants,
  openRelevantApplicationDialog,
}) => {
  const { t } = useTranslations(translations);

  return (
    <>
      {billableApplicants ? (
        <Inline alignY="center" space="xsmall">
          {billableApplicants.success ? (
            <Box
              cursor="pointer"
              onClick={() => {
                openRelevantApplicationDialog();
              }}
            >
              <Inline space="xxsmall" alignY="center">
                <Box pointerEvents="none">
                  <Badge tone="promote">
                    {billableApplicants.billableApplicantsCount}/
                    {billableApplicants.maxBillableApplicants}
                  </Badge>
                </Box>
                <TextLinkButton>
                  <Text size="small" tone="secondary">
                    {t('Relevant applications')}
                  </Text>
                </TextLinkButton>
              </Inline>
            </Box>
          ) : (
            <TextLinkButton
              onClick={() => {
                openRelevantApplicationDialog();
              }}
            >
              <Text tone="secondary" size="small">
                {t('Relevant applications')}
              </Text>
            </TextLinkButton>
          )}
        </Inline>
      ) : null}
    </>
  );
};

interface ReferenceCheckBalanceLinkProps {
  referenceBalance?: ComponentProps<
    typeof ReferenceCheckBalanceDialog
  >['referenceBalance'];
  onClick?: () => void;
  trackReferenceCheckBalanceDialogDisplayedEvent: () => void;
  trackReferenceCheckBalanceDialogDismissedEvent: () => void;
}

export const ReferenceCheckBalanceLink: FC<ReferenceCheckBalanceLinkProps> = ({
  onClick,
  referenceBalance,
  trackReferenceCheckBalanceDialogDisplayedEvent,
  trackReferenceCheckBalanceDialogDismissedEvent,
}) => {
  const { t } = useTranslations(translations);
  const [open, setOpen] = useState(false);

  if (!referenceBalance) {
    return null;
  }

  const handleClick = () => {
    setOpen(true);

    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      <Text
        size="small"
        tone="secondary"
        data={{ testid: 'reference-balance' }}
      >
        {t(
          '<Strong>{availableBalance}/{totalBalance}</Strong> <TextLinkButton>Reference checks</TextLinkButton>',
          {
            availableBalance: referenceBalance.availableBalance,
            totalBalance: referenceBalance.totalBalance,
            Strong: (children) => <Strong>{children}</Strong>,
            TextLinkButton: (children) => (
              <TextLinkButton weight="weak" onClick={handleClick}>
                {children}
              </TextLinkButton>
            ),
          },
        )}
      </Text>
      {open && (
        <ReferenceCheckBalanceDialog
          open={open}
          onClose={() => setOpen(false)}
          referenceBalance={referenceBalance}
          trackReferenceCheckBalanceDialogDisplayedEvent={
            trackReferenceCheckBalanceDialogDisplayedEvent
          }
          trackReferenceCheckBalanceDialogDismissedEvent={
            trackReferenceCheckBalanceDialogDismissedEvent
          }
        />
      )}
    </>
  );
};

export const JobStatDetailLayout: FC<{
  placementStatusLink: JSX.Element | null;
  expireDate: JSX.Element | null;
  totalApplications: JSX.Element;
  billableApplicants: JSX.Element | null;
  referenceCheckBalance: JSX.Element | null;
}> = ({
  placementStatusLink,
  expireDate,
  totalApplications,
  billableApplicants,
  referenceCheckBalance,
}) => (
  <Box data-testid="job-stats-summary">
    <Display mobile>
      <Stack space="small">
        <Stack space="medium">
          {placementStatusLink}
          <Inline space="small" alignY="center">
            {expireDate}
            {totalApplications}
            {referenceCheckBalance}
          </Inline>
        </Stack>
        {billableApplicants}
      </Stack>
    </Display>

    <Display tablet desktop>
      <Inline space="small" alignY="center">
        {placementStatusLink}
        {expireDate}
        {totalApplications}
        {billableApplicants}
        {referenceCheckBalance}
      </Inline>
    </Display>
  </Box>
);
